import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { linkedinOAuth } from '../services/oauth/linkedinOAuth';
import { LinkedInIcon, FacebookIcon, TwitterIcon, InstagramIcon, ShareOutIcon } from './icons';

interface SocialSharingOptionsProps {
  onSelectionChange?: (platforms: string[]) => void;
  className?: string;
  compact?: boolean;
}

const SocialSharingOptions: React.FC<SocialSharingOptionsProps> = ({ 
  onSelectionChange, 
  className = '',
  compact = false
}) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectedPlatforms, setConnectedPlatforms] = useState<Record<string, boolean>>({});
  const [connectPromptPlatform, setConnectPromptPlatform] = useState<string | null>(null);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, color: 'text-blue-600' },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: 'text-blue-700' },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, color: 'text-black' },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, color: 'text-pink-600' }
  ];

  useEffect(() => {
    loadUserPreferences();
    checkConnectedPlatforms();
    
    // Poll for connection changes every 5 seconds to stay in sync
    const intervalId = setInterval(() => {
      checkConnectedPlatforms();
    }, 5000);
    
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedPlatforms);
    }
  }, [selectedPlatforms, onSelectionChange]);

  const loadUserPreferences = async () => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'userProfiles', auth.currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const savedPlatforms = userData.defaultSocialSharing || [];
        setSelectedPlatforms(savedPlatforms);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkConnectedPlatforms = async () => {
    if (!auth.currentUser) return;

    try {
      const connections: Record<string, boolean> = {};
      
      // Check LinkedIn connection. Support both the new socialConnections doc
      // and older profile fields so connected users can still toggle sharing.
      const [linkedInConnected, profileDoc] = await Promise.all([
        linkedinOAuth.isConnected(auth.currentUser.uid),
        getDoc(doc(db, 'userProfiles', auth.currentUser.uid))
      ]);
      const profileData = profileDoc.exists() ? profileDoc.data() : {};
      connections.linkedin = linkedInConnected || Boolean(profileData.linkedinAccessToken || profileData.linkedin?.accessToken || profileData.linkedin);
      console.log('🔗 LinkedIn connection status:', connections.linkedin);
      
      // TODO: Add other platform checks when implemented
      connections.facebook = false;
      connections.twitter = false;
      connections.instagram = false;
      
      setConnectedPlatforms(connections);
      
      // Auto-deselect disconnected platforms
      setSelectedPlatforms(prev => {
        const stillConnected = prev.filter(platformId => connections[platformId]);
        if (stillConnected.length !== prev.length) {
          console.log('🔄 Auto-removed disconnected platforms from selection');
        }
        return stillConnected;
      });
    } catch (error) {
      console.error('Error checking connected platforms:', error);
    }
  };

  const togglePlatform = async (platformId: string) => {
    if (!connectedPlatforms[platformId]) return;

    const newSelection = selectedPlatforms.includes(platformId)
      ? selectedPlatforms.filter(p => p !== platformId)
      : [...selectedPlatforms, platformId];

    setSelectedPlatforms(newSelection);

    // Save to user preferences
    if (auth.currentUser) {
      try {
        await setDoc(doc(db, 'userProfiles', auth.currentUser.uid), {
          defaultSocialSharing: newSelection
        }, { merge: true });
      } catch (error) {
        console.error('Error saving user preferences:', error);
      }
    }
  };

  const openConnectPrompt = (platformId: string) => {
    setConnectPromptPlatform(platformId);
  };

  const handleConnectPlatform = () => {
    if (connectPromptPlatform === 'linkedin') {
      sessionStorage.setItem('linkedinReturnUrl', window.location.hash || '#dashboard');
      linkedinOAuth.startOAuthFlow();
    }
  };

  if (loading) {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        Laden...
      </div>
    );
  }

  const promptPlatform = platforms.find((item) => item.id === connectPromptPlatform);

  return (
    <div className={`${compact ? 'space-y-1.5' : 'space-y-2'} ${className}`}>
      <div className={`text-gray-600 font-medium flex items-center gap-1.5 ${compact ? 'text-[11px]' : 'text-xs'}`}>
        <ShareOutIcon size={compact ? 12 : 14} weight="regular" />
        <span>Deel ook naar:</span>
      </div>
      <div className={compact ? 'grid grid-cols-1 gap-2' : 'flex flex-wrap gap-3'}>
        {platforms.map((platform) => {
          const isConnected = connectedPlatforms[platform.id] || false;
          const isSelected = selectedPlatforms.includes(platform.id);
          if (compact) {
            return (
              <button
                type="button"
                key={platform.id}
                onClick={() => isConnected ? togglePlatform(platform.id) : openConnectPrompt(platform.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  minHeight: 38,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1px solid ${isSelected ? '#7A9488' : isConnected ? '#E5E7EB' : '#F1F2EF'}`,
                  background: isSelected ? '#F0F7F4' : isConnected ? '#FFFFFF' : '#FAFAF8',
                  opacity: isConnected ? 1 : 0.62,
                  cursor: 'pointer',
                  textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 4,
                    border: `1px solid ${isConnected ? '#8DA8A0' : '#D1D5DB'}`,
                    background: isSelected ? '#7A9488' : isConnected ? '#FFFFFF' : '#F3F4F6',
                    color: '#FFFFFF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isSelected && (
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.1 4.8 8.3 9.5 3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <platform.Icon size={15} weight="regular" className={isConnected ? platform.color : 'text-gray-400'} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    color: isConnected ? '#374151' : '#9CA3AF',
                    fontSize: 13,
                    fontWeight: isConnected ? 600 : 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {platform.name}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    borderRadius: 999,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: isConnected ? (isSelected ? '#FFFFFF' : '#4F6660') : '#9CA3AF',
                    background: isConnected ? (isSelected ? '#7A9488' : '#E8F1ED') : '#F3F4F6',
                  }}
                >
                  {isConnected ? (isSelected ? 'Aan' : 'Gekoppeld') : 'Niet gekoppeld'}
                </span>
              </button>
            );
          }
          
          return (
            <button
              type="button"
              key={platform.id}
              onClick={() => isConnected ? togglePlatform(platform.id) : openConnectPrompt(platform.id)}
              className={`flex w-full items-center text-left transition-colors ${
                compact ? 'gap-2 rounded-lg border px-3 py-2.5 min-w-0' : 'space-x-2 px-2 py-1 rounded'
              } ${
                isConnected
                  ? isSelected
                    ? 'cursor-pointer border-[#7A9488] bg-[#F0F7F4]'
                    : 'cursor-pointer border-gray-200 bg-white hover:bg-gray-50'
                  : 'cursor-pointer border-gray-100 bg-gray-50 opacity-60'
              }`}
            >
              <span
                aria-hidden="true"
                className={`${compact ? 'h-4 w-4' : 'h-4 w-4'} flex flex-shrink-0 items-center justify-center rounded-[3px] border ${
                  isConnected
                    ? isSelected
                      ? 'border-[#7A9488] bg-[#7A9488] text-white'
                      : 'border-[#8DA8A0] bg-white'
                    : 'border-gray-300 bg-gray-100'
                }`}
              >
                {isSelected && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.1 4.8 8.3 9.5 3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className={`${compact ? 'text-xs gap-1.5' : 'text-sm gap-1.5'} flex flex-1 items-center min-w-0`}>
                <platform.Icon size={compact ? 15 : 16} weight="regular" className={`${isConnected ? platform.color : 'text-gray-400'} flex-shrink-0`} />
                <span className={`${isConnected ? 'text-gray-800 font-medium' : 'text-gray-400'} truncate`}>{platform.name}</span>
                {isConnected ? (
                  <span className={`${compact ? 'ml-auto text-[11px]' : 'ml-1 text-xs'} flex-shrink-0 rounded-full px-2 py-0.5 ${isSelected ? 'bg-[#7A9488] text-white' : 'bg-[#E8F1ED] text-[#4F6660]'}`}>
                    {isSelected ? 'Aan' : 'Gekoppeld'}
                  </span>
                ) : (
                  <span className={`${compact ? 'ml-auto text-[11px]' : 'ml-1 text-xs'} flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-gray-400`}>Niet gekoppeld</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {connectPromptPlatform && (
        <div className="mt-2 rounded-xl border border-[#DDE8E3] bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800">
                {promptPlatform?.name || 'Kanaal'} koppelen
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {connectPromptPlatform === 'linkedin'
                  ? 'Koppel LinkedIn om dit bericht direct mee te delen.'
                  : 'Koppelen voor dit kanaal is nog niet beschikbaar. LinkedIn kan al gekoppeld worden.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setConnectPromptPlatform(null)}
              className="text-gray-400"
              aria-label="Koppelkaart sluiten"
            >
              ×
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            {connectPromptPlatform === 'linkedin' && (
              <button
                type="button"
                onClick={handleConnectPlatform}
                className="flex-1 rounded-lg bg-[#7A9488] px-3 py-2 text-sm font-semibold text-white"
              >
                Koppel LinkedIn
              </button>
            )}
            <button
              type="button"
              onClick={() => setConnectPromptPlatform(null)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialSharingOptions;
